import React, { useState, useEffect } from 'react';
import { useAuth } from '@clerk/clerk-react';
import { useTheme } from '../../contexts/ThemeContext.jsx';
import { useToast } from '../../contexts/ToastContext.jsx';
import NotificationsTable from '../../components/admin/NotificationsTable.jsx';
import NotificationForm from '../../components/admin/NotificationForm.jsx';
import NotificationDetails from '../../components/admin/NotificationDetails.jsx';
import ApiClient from '../../utils/apiClient.js';
import LoadingSpinner from '../../components/LoadingSpinner.jsx';

/**
 * AdminNotifications component for managing system notifications
 * Implements theme-aware styling and follows existing admin patterns
 */
const AdminNotifications = () => {
  const { getToken } = useAuth();
  const { theme } = useTheme();
  const { showToast } = useToast();
  const [loading, setLoading] = useState(true);
  const [notifications, setNotifications] = useState([]);
  const [selectedNotification, setSelectedNotification] = useState(null);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [showEditForm, setShowEditForm] = useState(false);
  const [editingNotification, setEditingNotification] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [error, setError] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [deleting, setDeleting] = useState(false);

  // Initialize API client
  const apiClient = new ApiClient(getToken);

  // Load notifications on component mount
  useEffect(() => {
    loadNotifications();
  }, [searchTerm, statusFilter]);

  /**
   * Load notifications from API with filtering and enhanced error handling
   */
  const loadNotifications = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const params = new URLSearchParams();
      if (searchTerm) params.append('search', searchTerm);
      if (statusFilter !== 'all') params.append('active', statusFilter === 'active' ? 'true' : 'false');
      
      const response = await apiClient.get(`/api/admin/notifications?${params}`);
      
      if (response.success) {
        setNotifications(response.notifications || []);
      } else {
        // Handle structured error responses
        if (response.error && response.error.code === 'AUTHORIZATION_ERROR') {
          setError('Access denied. Admin permission required.');
          showToast('Access denied. Admin permission required.', 'error');
        } else if (response.error && response.error.code === 'VALIDATION_ERROR') {
          const validationErrors = response.error.details?.validation_errors || {};
          const errorMessages = Object.values(validationErrors).join(', ');
          setError(`Invalid parameters: ${errorMessages}`);
          showToast(`Invalid parameters: ${errorMessages}`, 'error');
        } else {
          throw new Error(response.error?.message || response.message || 'Failed to load notifications');
        }
      }
    } catch (error) {
      console.error('Failed to load notifications:', error);
      
      // Handle different types of errors
      if (error.name === 'TypeError' && error.message.includes('fetch')) {
        setError('Network error. Please check your connection and try again.');
        showToast('Network error. Please check your connection and try again.', 'error');
      } else if (error.message.includes('401') || error.message.includes('unauthorized')) {
        setError('Session expired. Please refresh the page and try again.');
        showToast('Session expired. Please refresh the page and try again.', 'error');
      } else if (error.message.includes('403') || error.message.includes('forbidden')) {
        setError('Access denied. Admin permission required.');
        showToast('Access denied. Admin permission required.', 'error');
      } else if (error.message.includes('500')) {
        setError('Server error. Please try again later.');
        showToast('Server error. Please try again later.', 'error');
      } else {
        setError(error.message || 'Failed to load notifications. Please try again.');
        showToast(error.message || 'Failed to load notifications', 'error');
      }
    } finally {
      setLoading(false);
    }
  };

  /**
   * Handle notification selection for details view with enhanced error handling
   */
  const handleNotificationSelect = async (notification) => {
    try {
      // Load full notification details including revision history
      const response = await apiClient.get(`/api/admin/notifications/${notification.id}`);
      
      if (response.success) {
        setSelectedNotification(response.notification);
      } else {
        // Handle structured error responses
        if (response.error && response.error.code === 'AUTHORIZATION_ERROR') {
          showToast('Access denied. Admin permission required.', 'error');
        } else if (response.error && response.error.code === 'RESOURCE_NOT_FOUND') {
          showToast('Notification not found. It may have been deleted.', 'error');
          // Remove from local state
          setNotifications(prev => prev.filter(n => n.id !== notification.id));
        } else {
          throw new Error(response.error?.message || response.message || 'Failed to load notification details');
        }
      }
    } catch (error) {
      console.error('Failed to load notification details:', error);
      
      // Handle different types of errors
      if (error.name === 'TypeError' && error.message.includes('fetch')) {
        showToast('Network error. Please check your connection and try again.', 'error');
      } else if (error.message.includes('401') || error.message.includes('unauthorized')) {
        showToast('Session expired. Please refresh the page and try again.', 'error');
      } else if (error.message.includes('403') || error.message.includes('forbidden')) {
        showToast('Access denied. Admin permission required.', 'error');
      } else if (error.message.includes('404')) {
        showToast('Notification not found. It may have been deleted.', 'error');
        // Remove from local state
        setNotifications(prev => prev.filter(n => n.id !== notification.id));
      } else {
        showToast(error.message || 'Failed to load notification details', 'error');
      }
    }
  };

  /**
   * Handle create notification button click
   */
  const handleCreateNotification = () => {
    setEditingNotification(null);
    setShowCreateForm(true);
    setShowEditForm(false);
  };

  /**
   * Handle edit notification
   */
  const handleEditNotification = (notification) => {
    setEditingNotification(notification);
    setShowEditForm(true);
    setShowCreateForm(false);
  };

  /**
   * Handle form success (create or edit)
   */
  const handleFormSuccess = () => {
    loadNotifications();
    setShowCreateForm(false);
    setShowEditForm(false);
    setEditingNotification(null);
  };

  /**
   * Handle form close
   */
  const handleFormClose = () => {
    setShowCreateForm(false);
    setShowEditForm(false);
    setEditingNotification(null);
  };

  /**
   * Handle search input change
   */
  const handleSearchChange = (value) => {
    setSearchTerm(value);
  };

  /**
   * Handle status filter change
   */
  const handleStatusFilterChange = (value) => {
    setStatusFilter(value);
  };

  /**
   * Handle notification deletion (hide globally)
   */
  const handleDeleteNotification = (notification) => {
    setDeleteConfirm(notification);
  };

  /**
   * Confirm and execute notification deletion
   */
  const confirmDeleteNotification = async () => {
    if (!deleteConfirm) return;

    try {
      setDeleting(true);
      const response = await apiClient.delete(`/api/admin/notifications/${deleteConfirm.id}`);
      
      if (response.success) {
        showToast('Notification deleted successfully', 'success');
        // Remove from local state
        setNotifications(prev => prev.filter(n => n.id !== deleteConfirm.id));
        // Clear selection if this notification was selected
        if (selectedNotification?.id === deleteConfirm.id) {
          setSelectedNotification(null);
        }
        setDeleteConfirm(null);
      } else {
        // Handle structured error responses
        if (response.error && response.error.code === 'AUTHORIZATION_ERROR') {
          showToast('Access denied. Admin permission required.', 'error');
        } else if (response.error && response.error.code === 'RESOURCE_NOT_FOUND') {
          showToast('Notification not found. It may have already been deleted.', 'error');
          // Remove from local state anyway
          setNotifications(prev => prev.filter(n => n.id !== deleteConfirm.id));
          setDeleteConfirm(null);
        } else {
          throw new Error(response.error?.message || response.message || 'Failed to delete notification');
        }
      }
    } catch (error) {
      console.error('Failed to delete notification:', error);
      
      // Handle different types of errors
      if (error.name === 'TypeError' && error.message.includes('fetch')) {
        showToast('Network error. Please check your connection and try again.', 'error');
      } else if (error.message.includes('401') || error.message.includes('unauthorized')) {
        showToast('Session expired. Please refresh the page and try again.', 'error');
      } else if (error.message.includes('403') || error.message.includes('forbidden')) {
        showToast('Access denied. Admin permission required.', 'error');
      } else if (error.message.includes('404')) {
        showToast('Notification not found. It may have already been deleted.', 'error');
        // Remove from local state anyway
        setNotifications(prev => prev.filter(n => n.id !== deleteConfirm.id));
        setDeleteConfirm(null);
      } else {
        showToast(error.message || 'Failed to delete notification', 'error');
      }
    } finally {
      setDeleting(false);
    }
  };



  return (
    <div>
      {/* Page Header */}
      <div className="mb-6">
        <h2 className={`text-3xl font-bold ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
          Notifications Management
        </h2>
        <p className={`mt-1 ${theme === 'dark' ? 'text-gray-300' : 'text-gray-600'}`}>
          Create and manage system notifications for users
        </p>
      </div>

      {/* Create Notification Button */}
      <div className="mb-6 flex justify-end">
        <button
          onClick={handleCreateNotification}
          className="px-6 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors focus:ring-2 focus:ring-teal-500 focus:ring-offset-2 flex items-center gap-2 dark:text-white"
        >
          <span className="text-lg">🔔</span>
          Create Notification
        </button>
      </div>

      {/* Error Display */}
      {error && (
        <div className={`mb-6 p-4 rounded-lg border ${
          theme === 'dark' 
            ? 'bg-red-900/20 border-red-500/30 text-red-400' 
            : 'bg-red-50 border-red-200 text-red-800'
        }`}>
          <div className="flex items-center gap-2">
            <span className="text-lg">⚠️</span>
            <span>{error}</span>
          </div>
          <button
            onClick={loadNotifications}
            className="mt-2 text-sm underline hover:no-underline"
          >
            Try again
          </button>
        </div>
      )}

      {/* Loading State */}
      {loading && (
        <div className="flex items-center justify-center py-12">
          <div className="text-center">
            <LoadingSpinner />
            <p className={`mt-4 text-sm ${theme === 'dark' ? 'text-gray-300' : 'text-gray-600'}`}>
              Loading notifications...
            </p>
          </div>
        </div>
      )}

      {/* Notifications Table */}
      {!error && (
        <NotificationsTable
          notifications={notifications}
          loading={loading}
          selectedNotification={selectedNotification}
          onNotificationSelect={handleNotificationSelect}
          onNotificationEdit={handleEditNotification}
          onNotificationDelete={handleDeleteNotification}
          searchTerm={searchTerm}
          onSearchChange={handleSearchChange}
          statusFilter={statusFilter}
          onStatusFilterChange={handleStatusFilterChange}
          onRefresh={loadNotifications}
        />
      )}

      {/* Notification Details Section */}
      {selectedNotification && (
        <div className="mt-6">
          <NotificationDetails
            notification={selectedNotification}
            onEdit={handleEditNotification}
            onRefresh={loadNotifications}
            onClose={() => setSelectedNotification(null)}
          />
        </div>
      )}

      {/* Create/Edit Form Section */}
      {(showCreateForm || showEditForm) && (
        <div className="mt-6">
          <NotificationForm
            notification={editingNotification}
            onClose={handleFormClose}
            onSuccess={handleFormSuccess}
          />
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {deleteConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className={`rounded-lg p-6 max-w-md w-full mx-4 border ${
            theme === 'dark' 
              ? 'bg-gray-800 border-gray-700' 
              : 'bg-white border-gray-200'
          }`}>
            <h3 className={`text-lg font-semibold mb-4 ${
              theme === 'dark' ? 'text-white' : 'text-gray-900'
            }`}>
              Delete Notification
            </h3>
            <p className={`mb-6 ${
              theme === 'dark' ? 'text-gray-300' : 'text-gray-600'
            }`}>
              Are you sure you want to delete "{deleteConfirm.title}"? This will hide it for all users and cannot be undone.
            </p>
            <div className="flex gap-3 justify-end">
              <button
                onClick={() => setDeleteConfirm(null)}
                disabled={deleting}
                className={`px-4 py-2 transition-colors disabled:opacity-50 ${
                  theme === 'dark' 
                    ? 'text-gray-300 hover:text-white' 
                    : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                Cancel
              </button>
              <button
                onClick={confirmDeleteNotification}
                disabled={deleting}
                className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg font-medium transition-colors disabled:opacity-50 dark:text-white"
              >
                {deleting ? 'Deleting...' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminNotifications;
